/**
 * TPL Cart Drawer — Slide-out cart with quantity controls
 * Extracted from tpl-cart-drawer.liquid
 * Listens for 'cart:item-added' event to open
 * Uses Section Rendering API for dynamic refresh
 */
class CartDrawer extends HTMLElement {
  constructor() {
    super();
    this._didAddItem = false;
    this.attachEventListeners();
  }

  attachEventListeners() {
    // Always re-query elements — references become stale after innerHTML refresh
    this.overlay = this.querySelector('[data-cart-drawer-overlay]');
    this.container = this.querySelector('[data-cart-drawer-container]');
    this.closeBtn = this.querySelector('[data-cart-drawer-close]');

    // Close drawer — use both click and touchend for mobile reliability
    if (this.closeBtn) {
      this.closeBtn.addEventListener('click', (e) => { e.stopPropagation(); this.close(); });
      this.closeBtn.addEventListener('touchend', (e) => { e.preventDefault(); e.stopPropagation(); this.close(); });
    }
    // Click outside drawer panel to close (works on both desktop and mobile)
    this.addEventListener('click', (e) => {
      if (this.container && !this.container.contains(e.target)) {
        this.close();
      }
    });

    // Escape key (guard against duplicate listeners on refresh)
    if (!this._keyHandler) {
      this._keyHandler = (e) => {
        if (e.key === 'Escape' && this.isOpen()) this.close();
      };
      document.addEventListener('keydown', this._keyHandler);
    }

    // Quantity changes
    this.querySelectorAll('[data-cart-qty-decrease]').forEach(btn => {
      btn.addEventListener('click', (e) => this.changeQuantity(e.target.closest('[data-key]').dataset.key, -1));
    });

    this.querySelectorAll('[data-cart-qty-increase]').forEach(btn => {
      btn.addEventListener('click', (e) => this.changeQuantity(e.target.closest('[data-key]').dataset.key, 1));
    });

    this.querySelectorAll('[data-cart-qty-input]').forEach(input => {
      input.addEventListener('change', (e) => {
        const newQty = parseInt(e.target.value);
        this.updateQuantity(e.target.dataset.key, newQty);
      });
    });

    // Remove item
    this.querySelectorAll('[data-cart-item-remove]').forEach(btn => {
      btn.addEventListener('click', (e) => this.removeItem(e.currentTarget.dataset.key));
    });

    // TPL: Glazing cross-sell add
    this.querySelectorAll('[data-tpl-glaze-add], [data-tpl-pair-add]').forEach(btn => {
      btn.addEventListener('click', (e) => {
        const target = e.currentTarget;
        const variantId = target.dataset.variantId;
        if (variantId) this.addGlazing(variantId, target);
      });
    });

    // Cart note
    const noteInput = this.querySelector('[data-cart-note]');
    if (noteInput) {
      noteInput.addEventListener('change', (e) => this.updateNote(e.target.value));
    }

    // Listen for cart updates from add-to-cart buttons (guard against duplicate listeners on refresh)
    if (!this._cartUpdatedHandler) {
      this._cartUpdatedHandler = () => this.refresh();
      document.addEventListener('cart:updated', this._cartUpdatedHandler);
    }
  }

  open() {
    // Capture current focus if Dawn's setActiveElement didn't (e.g., ATC auto-open)
    if (!this.activeElement && document.activeElement && document.activeElement !== document.body) {
      this.activeElement = document.activeElement;
    }
    this.classList.add('is-open');
    document.body.style.overflow = 'hidden';

    // Set inert on main content so screen readers stay in the drawer
    const mainContent = document.getElementById('MainContent');
    if (mainContent) mainContent.setAttribute('inert', '');

    // Focus trap — cycle Tab within the drawer
    if (!this._focusTrapHandler) {
      this._focusTrapHandler = (e) => {
        if (e.key !== 'Tab' || !this.isOpen()) return;
        const focusable = this.querySelectorAll(
          'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
        );
        if (focusable.length === 0) return;
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (e.shiftKey) {
          if (document.activeElement === first) { e.preventDefault(); last.focus(); }
        } else {
          if (document.activeElement === last) { e.preventDefault(); first.focus(); }
        }
      };
      document.addEventListener('keydown', this._focusTrapHandler);
    }

    // Move focus into the drawer
    requestAnimationFrame(() => {
      const closeBtn = this.querySelector('[data-cart-drawer-close]');
      if (closeBtn) closeBtn.focus();
    });
  }

  close() {
    this.classList.remove('is-open');
    document.body.style.overflow = '';

    // Remove inert from main content
    const mainContent = document.getElementById('MainContent');
    if (mainContent) mainContent.removeAttribute('inert');

    // WCAG 2.4.3 — Restore focus to the element that opened the drawer
    if (this.activeElement && typeof this.activeElement.focus === 'function') {
      this.activeElement.focus({ preventScroll: true });
      this.activeElement = null;
    }

    /* SPA cart refresh — replaces window.location.reload() after successful add
       on PDPs with glazing. Preserves scroll, avoids duplicate GA4 pageview,
       and resets PDP glazing UI in place. */
    if (this._didAddItem && document.getElementById('tpl-uv-glazing-checkbox')) {
      this._didAddItem = false;
      this.resetGlazingState();
    }
  }

  resetGlazingState() {
    const checkbox = document.getElementById('tpl-uv-glazing-checkbox');
    if (checkbox) checkbox.checked = false;
    const desc = document.getElementById('tpl-uv-glazing-desc');
    if (desc) desc.classList.remove('is-visible');
    const subtotal = document.getElementById('tpl-glazing-subtotal');
    if (subtotal) subtotal.hidden = true;

    const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    fetch(`${root}cart.js`)
      .then((r) => (r.ok ? r.json() : null))
      .then((cart) => {
        if (cart) {
          document.dispatchEvent(new CustomEvent('cart:updated', { detail: cart }));
          document.dispatchEvent(new CustomEvent('cart:count-updated', { detail: { count: cart.item_count } }));
        }
      })
      .catch(() => {});
  }

  isOpen() {
    return this.classList.contains('is-open');
  }

  /* Dawn contract — queried by Dawn's product-form.js via document.querySelector('cart-drawer').
     Prevents "TypeError: this.cart.getSectionsToRender is not a function" when Dawn's
     submit handler fires (guard at product-form.js:34 now passes). Section names match
     TPL theme: tpl-cart-drawer, cart-icon-bubble, cart-live-region-text. */
  getSectionsToRender() {
    return [
      { id: 'cart-drawer', section: 'tpl-cart-drawer', selector: 'cart-drawer' },
      { id: 'cart-icon-bubble', section: 'cart-icon-bubble', selector: '.shopify-section' },
      { id: 'cart-live-region-text', section: 'cart-live-region-text', selector: '.shopify-section' }
    ];
  }

  /* Dawn contract — called by product-form.js after successful add-to-cart.
     Delegates to TPL's own refresh() which pulls tpl-cart-drawer section HTML.
     TPL 2026-05-12 — open() runs first so the Dawn fallback path matches the
     primary tpl-cart-event-bridge path; otherwise an issue in the bridge would
     silently re-introduce the 3.3s stall this fix removes. Return value is
     unused by Dawn's product-form.js (verified at product-form.js:81,88). */
  renderContents() {
    this._didAddItem = true;
    this.showPendingItem(null);
    this.open();
    this.refresh();
  }

  /* TPL 2026-10-01 (Sept Clarity review, move 1) — on the FIRST add the drawer
     DOM still holds the empty template, so open()-before-refresh() slid in
     "Your collection is empty" for the length of the section fetch (150 ms on
     wifi, seconds in the IG in-app browser). 3 of 31 recorded ATC sessions
     tapped that message within a second of adding. Swap the empty block for a
     placeholder row built from the /cart/add.js payload; refresh() replaces it.
     No-op when the drawer already shows line items. */
  showPendingItem(detail) {
    const items = this.querySelector('[data-cart-items]');
    if (!items || !items.querySelector('.cart-empty')) return false;
    const item = detail && detail.item ? detail.item : null;
    items.replaceChildren(window.TPLCartDrawer.buildPendingRow(item, this.dataset.pendingText));
    this.classList.add('is-pending');
    return true;
  }

  /* Dawn contract — called by product-form.js to remember the button that triggered add. */
  setActiveElement(element) {
    this.activeElement = element;
  }

  changeQuantity(key, delta) {
    const input = this.querySelector(`[data-cart-qty-input][data-key="${key}"]`);
    if (!input) return;
    const currentQty = parseInt(input.value, 10) || 0;
    const newQty = Math.max(0, currentQty + delta);
    this.updateQuantity(key, newQty);
  }

  setItemLoading(key, loading) {
    const item = this.querySelector(`[data-cart-item="${key}"]`);
    if (!item) return;
    if (loading) {
      item.classList.add('cart-item--loading');
      item.style.pointerEvents = 'none';
    } else {
      item.classList.remove('cart-item--loading');
      item.style.pointerEvents = '';
    }
  }

  async updateQuantity(key, quantity) {
    this.setItemLoading(key, true);
    try {
      const response = await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: key, quantity })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error('Cart update failed:', errorData.description || response.statusText);
      }

      await this.refresh();
    } catch (error) {
      console.error('Error updating quantity:', error);
      await this.refresh();
    }
  }

  async removeItem(key) {
    this.setItemLoading(key, true);
    await this.updateQuantity(key, 0);
  }

  async addGlazing(variantId, button) {
    if (button) { button.disabled = true; button.setAttribute('aria-busy', 'true'); }
    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: [{ id: parseInt(variantId, 10), quantity: 1 }] })
      });
      if (!response.ok) throw new Error('add failed: ' + response.status);
      await this.refresh();
      // Notify the rest of the theme so cart count badges update
      document.dispatchEvent(new CustomEvent('cart:updated'));
    } catch (error) {
      console.error('Error adding glazing:', error);
      if (button) { button.disabled = false; button.removeAttribute('aria-busy'); }
    }
  }

  async updateNote(note) {
    try {
      await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ note })
      });
    } catch (error) {
      console.error('Error updating note:', error);
    }
  }

  async refresh() {
    try {
      // Use Section Rendering API with correct format
      const response = await fetch(`/?sections=tpl-cart-drawer`);
      if (!response.ok) return;

      const json = await response.json();
      const html = json['tpl-cart-drawer'];

      if (html) {
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');
        const newContent = doc.querySelector('cart-drawer');

        if (newContent) {
          this.innerHTML = newContent.innerHTML;
          this.classList.remove('is-pending');
          this.attachEventListeners();
          // Animate shipping bar fill after DOM swap
          const fill = this.querySelector('[data-shipping-fill]');
          if (fill) {
            const targetWidth = fill.style.width;
            fill.style.width = '0%';
            requestAnimationFrame(() => { fill.style.width = targetWidth; });
          }
        }
      }

      // Update cart count in header
      const cartResponse = await fetch('/cart.js');
      if (!cartResponse.ok) return;

      const cart = await cartResponse.json();
      document.dispatchEvent(new CustomEvent('cart:count-updated', { detail: { count: cart.item_count } }));
    } catch (error) {
      console.error('Error refreshing cart:', error);
    }
  }
}

/* Pure helper (tested in tests/theme/cart-drawer-pending.test.js). Builds the
   placeholder line item from a /cart/add.js payload; every field is optional
   and set through textContent so payload strings never reach innerHTML. */
window.TPLCartDrawer = window.TPLCartDrawer || {};
window.TPLCartDrawer.buildPendingRow = function (item, pendingText) {
  const row = document.createElement('div');
  row.className = 'cart-item cart-item--pending';
  row.setAttribute('data-cart-pending', '');
  row.setAttribute('aria-busy', 'true');

  const media = document.createElement('div');
  media.className = 'cart-item-image';
  const src = item && (item.image || (item.featured_image && item.featured_image.url));
  if (src) {
    const img = document.createElement('img');
    img.src = src;
    img.alt = '';
    img.width = 100;
    img.height = 100;
    media.appendChild(img);
  }
  row.appendChild(media);

  const details = document.createElement('div');
  details.className = 'cart-item-details';

  const titleRow = document.createElement('div');
  titleRow.className = 'cart-item-title-row';
  const title = document.createElement('span');
  title.className = 'cart-item-title';
  title.textContent = (item && (item.product_title || item.title)) || 'Your print';
  titleRow.appendChild(title);
  details.appendChild(titleRow);

  if (item && item.variant_title) {
    const meta = document.createElement('div');
    meta.className = 'cart-item-meta';
    const variant = document.createElement('span');
    variant.className = 'cart-item-variant';
    variant.textContent = item.variant_title;
    meta.appendChild(variant);
    details.appendChild(meta);
  }

  const note = document.createElement('p');
  note.className = 'cart-item-pending-note';
  note.setAttribute('role', 'status');
  note.textContent = pendingText || 'Adding to your collection…';
  details.appendChild(note);

  row.appendChild(details);
  return row;
};

customElements.define('cart-drawer', CartDrawer);

// Open cart drawer on add to cart
// TPL 2026-05-12 — open() runs immediately so the drawer slides in within ~50ms;
// previously refresh().then(open) blocked visual feedback for ~3.3s on 4G mobile
// while two section-render fetches resolved. The drawer DOM already holds the
// previous cart contents so the slide-in is non-empty; refresh() runs in
// parallel and swaps the line items in place once /?sections=tpl-cart-drawer
// + /cart.js return.
document.addEventListener('cart:item-added', (e) => {
  const drawer = document.querySelector('cart-drawer');
  if (drawer) {
    drawer._didAddItem = true;
    drawer.showPendingItem(e && e.detail);
    /* tpl-cart-fx.js sets detail.openDelay while the fly-to-cart thumb is in
       the air so the drawer slides in on the landing, not over the flight. */
    const openDelay = e && e.detail && Number(e.detail.openDelay) > 0 ? Number(e.detail.openDelay) : 0;
    if (openDelay) setTimeout(() => drawer.open(), openDelay);
    else drawer.open();
    drawer.refresh();
  }
});

// Open cart drawer when cart icon clicked
document.addEventListener('DOMContentLoaded', () => {
  const cartTrigger = document.querySelector('[data-cart-trigger]');
  if (cartTrigger) {
    cartTrigger.addEventListener('click', (e) => {
      e.preventDefault();
      const drawer = document.querySelector('cart-drawer');
      if (drawer) drawer.open();
    });
  }
});

// WCAG 2.1.1 — Delegated close handler replaces inline onclick on cart-drawer.liquid buttons
document.addEventListener('click', function (e) {
  const trigger = e.target.closest('[data-cart-drawer-close]');
  if (!trigger) return;
  const drawer = trigger.closest('cart-drawer, [data-tpl-cart-drawer]');
  if (drawer && typeof drawer.close === 'function') {
    e.preventDefault();
    drawer.close();
  }
});

/* 2026-10-20 (audit X1): copy a cart link that rebuilds this cart in another browser.
   The drawer section re-renders through the Section Rendering API, so the handler is
   delegated from document instead of bound to the button. */
document.addEventListener('click', (e) => {
  const btn = e.target.closest && e.target.closest('[data-tpl-cart-handoff-copy]');
  if (!btn) return;
  const box = btn.closest('[data-tpl-cart-handoff]');
  const link = box ? box.getAttribute('data-cart-link') : '';
  if (!link) return;
  const done = () => {
    btn.textContent = 'Copied. Paste it in Safari or Chrome';
    box.classList.add('is-copied');
  };
  const fallback = () => {
    const ta = document.createElement('textarea');
    ta.value = link;
    ta.setAttribute('readonly', '');
    ta.style.cssText = 'position:fixed;left:-9999px;top:0';
    document.body.appendChild(ta);
    ta.select();
    try {
      if (document.execCommand('copy')) done();
    } catch (err) {
      /* nothing to recover: the button keeps its label */
    }
    ta.remove();
  };
  if (navigator.clipboard && navigator.clipboard.writeText) {
    navigator.clipboard.writeText(link).then(done, fallback);
  } else {
    fallback();
  }
});
